import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  listToolPreferences,
  setToolPreference,
  type ChatbotToolPreference,
} from "@/stores/chat/repository";
import { resolveChatbotBaseUrl } from "@/lib/chatbotBaseUrl";
import { CHATBOT_AUTO_APPROVE_TOOLS } from "./chatbotTools";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

const CHATBOT_CONFIGURED = Boolean(resolveChatbotBaseUrl());

export function ChatbotSettingsSection() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();
  const copy = t.settings.chatbotSettings;

  const queryKey = ["chatbot-tool-preferences", userId];

  const { data: preferences, isLoading } = useQuery({
    queryKey,
    queryFn: listToolPreferences,
    enabled: CHATBOT_CONFIGURED && Boolean(userId),
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: ({ toolName, enabled }: { toolName: string; enabled: boolean }) =>
      setToolPreference(toolName, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      // Re-fetch so the switch snaps back to the server's truth.
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: copy.title,
        description: copy.updateError,
        variant: "destructive",
      });
    },
  });

  if (!CHATBOT_CONFIGURED) {
    return null;
  }

  const isEnabled = (toolName: string): boolean =>
    Boolean(
      preferences?.find((p: ChatbotToolPreference) => p.tool_name === toolName)?.auto_approve
    );

  return (
    <SettingsSection
      label={copy.title}
      {...pageInfoTarget("settings.chatbot")}
      description={
        <>
          {copy.descriptionLine1}
          <br />
          {copy.descriptionLine2}
        </>
      }
    >
      <div className="divide-y divide-border">
        {CHATBOT_AUTO_APPROVE_TOOLS.map((tool) => {
          const toolCopy = copy.tools[tool.copyKey];
          const enabled = isEnabled(tool.name);
          const switchId = `chatbot-tool-${tool.name}`;
          return (
            // Control-first, matching the email-notification checkbox rows above.
            <div key={tool.name} className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
              <Switch
                id={switchId}
                checked={enabled}
                disabled={isLoading || mutation.isPending}
                aria-label={enabled ? copy.disableAria : copy.enableAria}
                onCheckedChange={(checked) =>
                  mutation.mutate({ toolName: tool.name, enabled: checked })
                }
                className="mt-0.5 data-[state=checked]:bg-success"
              />
              <div className="space-y-0.5">
                <Label htmlFor={switchId} className="cursor-pointer">
                  {toolCopy.label}
                </Label>
                <p className="text-sm text-muted-foreground">{toolCopy.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </SettingsSection>
  );
}
